import { TypedDocumentNode, gql } from '@apollo/client';
import { faFilter } from '@fortawesome/pro-solid-svg-icons';
import { useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';

import { Sport } from '@sorare/core/src/__generated__/globalTypes';
import { IconButton } from '@sorare/core/src/atoms/buttons/IconButton';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { LoadingIndicator } from '@sorare/core/src/atoms/loader/LoadingIndicator';
import { DetailsDialogBanner } from '@sorare/core/src/components/collections/DetailsDialogBanner';
import { ProgressBarThreshold } from '@sorare/core/src/components/collections/ProgressBar';
import { DesktopCollectionsFilters } from '@sorare/core/src/components/collections/filters/DesktopCollectionsFilters';
import { MobileCollectionsFilters } from '@sorare/core/src/components/collections/filters/MobileCollectionsFilters';
import { NoResults } from '@sorare/core/src/components/collections/filters/NoResults';
import { SearchFilter } from '@sorare/core/src/components/collections/filters/SearchFilter';
import {
  CollectionsFiltersState,
  StringifiedCollectionsFiltersState,
} from '@sorare/core/src/components/collections/filters/types';
import {
  parseFiltersState,
  stringifyFiltersState,
} from '@sorare/core/src/components/collections/filters/utils';
import { PageHeader } from '@sorare/core/src/components/navigation/PageHeader';
import {
  LeftFilters,
  SearchLayoutContainer,
  SearchLayoutMain,
  VerticalScroll,
} from '@sorare/core/src/components/search/SearchLayout/ui';
import { ANY_SPORT_MY_CLUB_SLUG_COLLECTIONS_COLLECTION } from '@sorare/core/src/constants/routes';
import { useCurrentUserContext } from '@sorare/core/src/contexts/currentUser';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import useScreenSize from '@sorare/core/src/hooks/device/useScreenSize';
import usePaginatedQuery from '@sorare/core/src/hooks/graphql/usePaginatedQuery';
import useInfiniteScroll from '@sorare/core/src/hooks/useInfiniteScroll';
import { useQueryState } from '@sorare/core/src/hooks/useQueryState';
import useToggle from '@sorare/core/src/hooks/useToggle';
import { navLabels } from '@sorare/core/src/lib/glossary';
import { mobileAndAbove } from '@sorare/core/src/style/mediaQuery';

import { CollectionNavigationSwitch } from '@sorare/marketplace/src/searchCards/CollectionNavigationSwitch';

import { CollectionPreview } from 'components/collections/CollectionPreview';
import { useCardCount } from 'hooks/useCardCount';

import {
  MyCardsCollectionsQuery,
  MyCardsCollectionsQueryVariables,
} from './__generated__/index.graphql';

const MobileSearchInputWrapper = styled.div`
  width: auto;
`;
const MobileNavigationSwitchWrapper = styled.div`
  flex-shrink: 0;
`;
const CollectionsListWrapper = styled.div.attrs({ role: 'list' })`
  display: grid;

  grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
  gap: var(--double-unit);

  @media ${mobileAndAbove} {
    grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  }
`;

const MY_CARDS_COLLECTIONS_QUERY = gql`
  query MyCardsCollectionsQuery(
    $user: String!
    $after: String
    $rarities: [Rarity!]
    $seasonStartYears: [Int!]
    $query: String
    $startedOnly: Boolean
    $sport: Sport!
  ) {
    user(slug: $user) {
      slug
      footballCardCounts {
        total
        common
      }
      cardCollections(
        startedOnly: $startedOnly
        after: $after
        first: 20
        rarities: $rarities
        seasonStartYears: $seasonStartYears
        query: $query
        sport: $sport
      ) {
        nodes {
          slug
          userCardCollection(forUserSlug: $user) {
            id
          }
          ...CollectionPreview_cardCollection
        }
        seasonsCount {
          count
          season {
            startYear
            name
          }
        }
        raritiesCount {
          count
          rarity
        }
        pageInfo {
          endCursor
          hasNextPage
        }
      }
      ...useCardCount_User
    }
  }
  ${useCardCount.fragments.user}
  ${CollectionPreview.fragments.cardCollection}
` as TypedDocumentNode<
  MyCardsCollectionsQuery,
  MyCardsCollectionsQueryVariables
>;

type Props = {
  thresholds?: ProgressBarThreshold[];
};
export const MyCardsCollectionsPage = ({ thresholds }: Props) => {
  const { sport = Sport.FOOTBALL, generateSportContextPath } =
    useSportContext();
  const { currentUser } = useCurrentUserContext();
  const [showFilters, toggleShowFilters] = useToggle(false);
  const [urlFiltersState, setUrlFiltersState] =
    useQueryState<StringifiedCollectionsFiltersState>({
      rarities: undefined,
      seasonStartYears: undefined,
      query: undefined,
      startedOnly: 'true',
      sport,
    });
  const { up: isLaptop } = useScreenSize('laptop');

  const [filtersState, setFiltersState] = useState<CollectionsFiltersState>(
    parseFiltersState(urlFiltersState)
  );

  const handleFiltersChange = useCallback(
    (newFiltersState: CollectionsFiltersState) => {
      setFiltersState(newFiltersState);
      setUrlFiltersState(stringifyFiltersState(newFiltersState));
    },
    [setUrlFiltersState]
  );

  const userSlug = currentUser?.slug || '';

  const { loading, data, previousData, loadMore } = usePaginatedQuery(
    MY_CARDS_COLLECTIONS_QUERY,
    {
      variables: {
        user: userSlug,
        query: filtersState.query,
        rarities: filtersState.rarities,
        seasonStartYears: filtersState.seasonStartYears,
        startedOnly: filtersState.startedOnly,
        sport,
      },
      connection: 'CardCollectionConnection',
    }
  );

  const collectionConnection = data?.user?.cardCollections;
  const previousCollectionConnection = previousData?.user?.cardCollections;
  const pageInfo = collectionConnection?.pageInfo;
  const hasMore = pageInfo?.hasNextPage || false;
  const after = pageInfo?.endCursor;
  const cardCounts = useCardCount(data?.user);

  const { InfiniteScrollLoader } = useInfiniteScroll(
    () => {
      loadMore(false, { user: userSlug || '', after });
    },
    hasMore,
    loading
  );

  const collectionsNodes = collectionConnection?.nodes || [];
  const connectionToDisplay =
    collectionConnection || previousCollectionConnection;

  const noResults = collectionsNodes.length === 0;
  const userHasCards = cardCounts ? cardCounts.total - cardCounts.common : 0;

  useEffect(() => {
    // need an effect because setUrlFiltersState is outside of this file
    if (!loading && !userHasCards && filtersState.startedOnly) {
      handleFiltersChange({ ...filtersState, startedOnly: false });
    }
  }, [handleFiltersChange, loading, userHasCards, filtersState]);

  if (!connectionToDisplay) {
    return <LoadingIndicator />;
  }

  return (
    <div>
      <PageHeader text={navLabels.collections} />

      <SearchLayoutContainer>
        {isLaptop && (
          <VerticalScroll>
            <LeftFilters visible>
              <SearchFilter
                filtersState={filtersState}
                onSearchChange={handleFiltersChange}
              />
              <DesktopCollectionsFilters
                raritiesCount={connectionToDisplay.raritiesCount}
                seasonsCount={connectionToDisplay.seasonsCount}
                onFiltersChange={handleFiltersChange}
                filtersState={filtersState}
              />
            </LeftFilters>
          </VerticalScroll>
        )}
        <SearchLayoutMain>
          {!isLaptop && (
            <Horizontal>
              <MobileSearchInputWrapper>
                <SearchFilter
                  filtersState={filtersState}
                  onSearchChange={handleFiltersChange}
                />
              </MobileSearchInputWrapper>
              <IconButton
                color="quaternary"
                onClick={toggleShowFilters}
                icon={faFilter}
              />
              <MobileNavigationSwitchWrapper>
                <CollectionNavigationSwitch />
              </MobileNavigationSwitchWrapper>
            </Horizontal>
          )}
          <Vertical gap={4}>
            {isLaptop && <CollectionNavigationSwitch />}
            <DetailsDialogBanner
              showShieldInfo
              showWarning
              thresholds={thresholds}
            />
            {noResults && loading ? (
              <LoadingIndicator grow />
            ) : (
              <>
                {noResults && <NoResults query={filtersState.query} />}

                <CollectionsListWrapper>
                  {collectionsNodes.map(collection => {
                    return (
                      <CollectionPreview
                        key={collection.slug}
                        collection={collection}
                        to={
                          userSlug
                            ? generateSportContextPath(
                                ANY_SPORT_MY_CLUB_SLUG_COLLECTIONS_COLLECTION,
                                {
                                  params: {
                                    slug: userSlug,
                                    collection: collection.slug,
                                    collectionSlug: collection.slug,
                                  },
                                }
                              )
                            : ''
                        }
                      />
                    );
                  })}
                  <InfiniteScrollLoader />
                </CollectionsListWrapper>
              </>
            )}
          </Vertical>
        </SearchLayoutMain>
      </SearchLayoutContainer>
      <MobileCollectionsFilters
        open={showFilters}
        raritiesCount={connectionToDisplay.raritiesCount}
        seasonsCount={connectionToDisplay.seasonsCount}
        onClose={toggleShowFilters}
        onFiltersChange={handleFiltersChange}
        filtersState={filtersState}
      />
    </div>
  );
};
